import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

// Components
import SpecialistCard from "~/components/cards/specialist/SpecialistCard";
import AdditionalHospitals from "~/components/common/AdditionalHospitals/AdditionalHospitals";
import CurrentChosenHospital from "~/components/common/CurrentChosenHospital/CurrentChosenHospital";
import NoFountEntity from "~/components/common/NoFoundEntity/NoFountEntity";
// Styles
import s from "~/views/appointment/specialist/SpecialistPage.module.scss";
import styles from "~/App.module.scss";
// Hooks
import { useStore } from "~/hooks/useStore";
import useIsMobileView from "~/hooks/useIsMobile";
// Types
import RootStore from "~/store/RootStore";
import { ISpecialistEntity } from "~/types/specialist";
// Utils
import { goToHelpPage } from "~/utils/helpers";
import ROUTES from "~/utils/routes";
import { logEvent } from "~/utils/amplitude";

interface Props {}

const SpecialistPage: React.FC<Props> = observer(({}) => {
  const store: RootStore = useStore();
  const specialistContainerRef = useRef(null);
  const firstRenderRef = useRef(true);
  const { isMobileView } = useIsMobileView();
  const hospitals = store.hospitalStore.additionalHospitals;
  const specialists = store.specialistStore.specialists;
  const displayedSpecialists = store.specialistStore.displayedSpecialists;
  const canGoToNextPage = store.specialistStore.pagination.canGoNextPage;
  const navigate = useNavigate();

  // ========================================Effects-START=======================================
  useEffect(() => {
    logEvent("Э/ Страница с списком врачей", {});
  }, []);

  useEffect(() => {
    if (
      store.cityStore.currentCity?.area_code &&
      store.specialityStore.currentSpeciality?.idSpecialty &&
      store.patientStore.currentPatient?.idUserData
    ) {
      store.specialistStore.fetchSpecialists({
        id_region: store.cityStore.currentCity.area_code,
        id_user_data: store.patientStore.currentPatient.idUserData,
        id_specialty: store.specialityStore.currentSpeciality.idSpecialty,
      });
    } else {
      navigate(ROUTES.HOSPITALS.PATHNAME, { replace: true });
    }
  }, [
    store.specialityStore,
    store.cityStore,
    store.patientStore,
    store.specialistStore,
    navigate,
  ]);

  useEffect(() => {
    if (isMobileView) {
      store.specialistStore.setWithPagination(true);
    } else {
      store.specialistStore.setWithPagination(false);
    }
  }, [isMobileView, store.specialistStore]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (specialistContainerRef.current) {
      (specialistContainerRef.current as HTMLElement).scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [store.hospitalStore.pagination.currentPage, specialistContainerRef]);
  // ========================================Effects-END=======================================

  const handleClickSpecialist = useCallback(
    (specialist: ISpecialistEntity) => {
      const foundSpecialist = store.specialistStore.specialists.find((spec) => {
        return spec.idDoctor == specialist.idDoctor;
      });

      if (foundSpecialist) {
        store.specialistStore.setSpecialist(foundSpecialist);

        store.cityStore.isMoscowRegion
          ? store.appointmentStepsStore.setCurrentStepForDisabling(1)
          : store.appointmentStepsStore.setCurrentStepForDisabling(2);

        store.cityStore.isMoscowRegion
          ? navigate(ROUTES.HOSPITALS.PATHNAME)
          : navigate(ROUTES.DATES.PATHNAME);
      }

      logEvent("Д/ Нажал плашку врача", {
        "ФИО врача": specialist.lname,
      });
    },
    [
      navigate,
      store.specialistStore,
      store.appointmentStepsStore,
      store.cityStore.isMoscowRegion,
    ]
  );

  const changeHospital = (id: number) => {
    store.hospitalStore.setCurrentHospital(id);
    navigate(ROUTES.SPECIALTIES.PATHNAME, { replace: true });
  };

  const handleShowMore = () => {
    store.specialistStore.pagination.goNextPage();
  };

  const resetPages = () => {
    store.specialistStore.pagination.setCurrentPage(1);
  };

  return (
    <div className="screen-container">
      {!store.cityStore.isMoscowRegion && (
        <div className={s.layoutCurrentHospital}>
          <CurrentChosenHospital />
        </div>
      )}

      {!!(!store.systemStore.loading && displayedSpecialists.length) && (
        <>
          <div className={styles.appointmentHeadingWrap}>
            <h2 className="headline-2">Доступные врачи для записи:</h2>
          </div>

          <div className={s.layoutCards}>
            {displayedSpecialists.map((specialist) => {
              return (
                <SpecialistCard
                  key={specialist.idDoctor}
                  disabled={false}
                  specialist={specialist}
                  speciality={
                    store.specialityStore.currentSpeciality?.name ?? ""
                  }
                  onClick={() => handleClickSpecialist(specialist)}
                />
              );
            })}
          </div>
        </>
      )}

      {!!(!store.systemStore.loading && !displayedSpecialists.length) && (
        <div className={s.hospitalNoFoundData}>
          <NoFountEntity
            title="Нет подходящих врачей"
            subtitle="Не нашли врачей? Расскажите нам об этом!"
            buttonText="Не нашли врачей"
            onClick={goToHelpPage}
          />
        </div>
      )}

      {isMobileView && specialists.length > 3 && (
        <div className={s.buttonShowMore}>
          <button
            className="button button--outlined"
            onClick={canGoToNextPage ? handleShowMore : resetPages}
          >
            {canGoToNextPage ? "Показать еще" : "Скрыть"}
          </button>
        </div>
      )}

      {!store.cityStore.isMoscowRegion && (
        <AdditionalHospitals hospitals={hospitals} onClick={changeHospital} />
      )}
    </div>
  );
});

export default SpecialistPage;
